import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight, CalendarDays, Clock, MapPin } from 'lucide-react'
import type { Event, Media, Team } from '@/payload-types'
import {
  attendanceLabel,
  eventDateTimeAttribute,
  formatEventDate,
  formatEventLocation,
  formatEventTime,
  teamMemberPrefix,
} from '@/lib/events'
import { getMediaUrl } from '../../lib/media'

interface EventCardProps {
  event: Event
  /** Past events read as an archive: muted, no registration call-to-action. */
  variant?: 'upcoming' | 'past'
  priority?: boolean
}

export function EventCard({ event, variant = 'upcoming', priority = false }: EventCardProps) {
  const isPast = variant === 'past'
  const heroImage = event.heroImage as Media | null | undefined
  const imageUrl = getMediaUrl(heroImage, 'card')
  const dateLabel = formatEventDate(event)
  const timeLabel = formatEventTime(event)
  const locationLabel = formatEventLocation(event)
  const teamMembers = (event.teamMembers || []).filter(
    (member): member is Team => typeof member === 'object' && member !== null,
  )

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-300 hover:shadow-xl focus-within:shadow-xl ${
        isPast ? 'opacity-90' : ''
      }`}
    >
      <div className="relative aspect-16/9 w-full overflow-hidden bg-brand-blue">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt=""
            fill
            priority={priority}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-brand-blue to-gray-950" />
        )}

        <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
          {attendanceLabel(event)}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="mb-3 text-xl font-serif text-primary transition-colors group-hover:text-brand-gold">
          <Link
            href={`/events/${event.slug}`}
            className="outline-none after:absolute after:inset-0 after:content-[''] focus-visible:underline"
          >
            {event.title}
          </Link>
        </h3>

        <dl className="mb-4 space-y-2 text-sm text-gray-600">
          {dateLabel && (
            <div className="flex items-start gap-2">
              <dt className="sr-only">Date</dt>
              <CalendarDays
                className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold"
                aria-hidden="true"
              />
              <dd>
                <time dateTime={eventDateTimeAttribute(event.startDate)}>{dateLabel}</time>
              </dd>
            </div>
          )}
          {timeLabel && (
            <div className="flex items-start gap-2">
              <dt className="sr-only">Time</dt>
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" aria-hidden="true" />
              <dd>{timeLabel}</dd>
            </div>
          )}
          {locationLabel && (
            <div className="flex items-start gap-2">
              <dt className="sr-only">Location</dt>
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" aria-hidden="true" />
              <dd>{locationLabel}</dd>
            </div>
          )}
        </dl>

        {event.shortDescription && (
          <p className="mb-4 line-clamp-3 text-sm text-gray-600">{event.shortDescription}</p>
        )}

        {teamMembers.length > 0 && (
          <p className="mb-4 text-xs text-gray-500">
            {teamMemberPrefix(event)}: {teamMembers.map((member) => member.name).join(', ')}
          </p>
        )}

        <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-brand-blue transition-colors group-hover:text-brand-gold">
          View event details
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
    </article>
  )
}
